import { Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, PanelLeftClose } from "lucide-react";
import Sidebar from "./Sidebar";
import AppuntiRapidiButton from "./AppuntiRapidi";
import { DemoBanner } from "./DemoBanner";
import { NewMessageNotifier } from "./NewMessageNotifier";
import { ShareOpenNotifier } from "./ShareOpenNotifier";
import { FirstAccessNotifier } from "./FirstAccessNotifier";
import { AbbonamentoNotifier } from "./AbbonamentoNotifier";
import { ImpersonationBar } from "./ImpersonationBar";
import SendStatusOverlay from "./SendStatusOverlay";
import { useAuth } from "../context/AuthContext";
import { Toaster } from "../components/ui/sonner";
import { TextSizeControl, LiveClock } from "./HeaderTools";
import PrivacyGate from "./PrivacyGate";
import api from "../lib/api";

const SMALL_QUERY = "(max-width: 1279px)";

function useIsSmall() {
  const [small, setSmall] = useState(() => window.matchMedia(SMALL_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(SMALL_QUERY);
    const on = (e) => setSmall(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return small;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const imp = !!user?.impersonating;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pgp_sidebar_collapsed") === "1");
  const small = useIsSmall();

  const toggleSidebar = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("pgp_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };
  const isClosed = collapsed;

  return (
    <div className="flex min-h-screen bg-background">
      {(!small || !collapsed) && <Sidebar collapsed={!small && collapsed} />}
      <main className="flex-1 min-w-0 pgp-grid-bg">
        <header
          className="sticky top-0 z-30 flex items-center justify-between gap-3 h-14 px-4 lg:px-6 2xl:px-10 border-b border-[#1e293b]"
          style={{ background: "color-mix(in srgb, var(--pgp-bg) 82%, transparent)", backdropFilter: "blur(8px)" }}
          data-testid="app-header"
        >
          <div className="flex items-center gap-2">
          <button
            onClick={toggleSidebar}
            data-testid="sidebar-toggle"
            title={isClosed ? "Apri menu laterale" : "Chiudi menu laterale"}
            aria-label={isClosed ? "Apri menu laterale" : "Chiudi menu laterale"}
            className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition-colors"
          >
            {isClosed ? <Menu className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
            <TextSizeControl />
          </div>
          <div className="flex items-center gap-3">
            <LiveClock />
            <AppuntiRapidiButton />
          </div>
        </header>
        <DemoBanner />
        <div className="w-full px-4 lg:px-6 2xl:px-10 py-6 2xl:py-8" data-testid="app-content">
          <Outlet />
        </div>
      </main>
      {!imp && <NewMessageNotifier />}
      {!imp && <ShareOpenNotifier />}
      {!imp && <FirstAccessNotifier />}
      {!imp && <AbbonamentoNotifier />}
      {!imp && user && <PrivacyGate client={api} onLogout={logout} />}
      <ImpersonationBar />
      <Toaster position="top-center" theme="light" richColors />
      <SendStatusOverlay />
    </div>
  );
}
